import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Form, InputGroup } from 'react-bootstrap';
import './OrdersList.css';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { getStateStyle, formatPrice, formatDate, MultiItemCell, RatecardItemsCell } from '../components/OrderCells';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

const ITEMS_PER_PAGE = 50;

/** Sloupce výpisu v pořadí hlavičky — klíče, pod kterými se ukládají šířky. */
const SLOUPCE = [
  'cislo', 'vytvoreno', 'klient', 'medium', 'projekt', 'inzeraty',
  'obchodnik', 'stav', 'cena', 'akce',
];

function OrdersList({ openTab }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [responsiblePersonFilter, setResponsiblePersonFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('date_of_creation');
  const [sortOrder, setSortOrder] = useState('desc');
  const [orders, setOrders] = useState([]);
  const [states, setStates] = useState([]);
  const [responsiblePersons, setResponsiblePersons] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('orders.list', SLOUPCE);

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getOrders({
        search: debouncedSearch,
        state: stateFilter,
        responsiblePerson: responsiblePersonFilter,
        dateFrom: dateFrom,
        dateTo: dateTo,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sort: sortColumn,
        order: sortOrder,
      });

      setOrders(result.data || []);
      if (result.states) {
        setStates(result.states);
      }
      if (result.responsiblePersons) {
        setResponsiblePersons(result.responsiblePersons);
      }
      const pagination = result.pagination || {};
      setTotalPages(pagination.totalPages || 1);
      setTotalItems(pagination.total || 0);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, stateFilter, responsiblePersonFilter, dateFrom, dateTo, currentPage, sortColumn, sortOrder]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Otevřená záložka Objednávky se sama zaktualizuje po uložení objednávky.
  useEffect(() => {
    const handler = () => fetchOrders();
    window.addEventListener('orders:refresh', handler);
    return () => window.removeEventListener('orders:refresh', handler);
  }, [fetchOrders]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const safePage = Math.min(currentPage, totalPages || 1);

  const openOrderEdit = (order) => openTab(
    `order-edit-${order.id}`,
    `Objednávka ${order.number || order.id}`,
    `/orders/${order.id}/edit`,
    'OrderForm',
    { orderId: order.id }
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
    return sortOrder === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  };

  const handleStateChange = (e) => {
    setStateFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleResponsiblePersonChange = (id) => {
    setResponsiblePersonFilter(id || '');
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStateFilter('');
    setResponsiblePersonFilter('');
    setDateFrom('');
    setDateTo('');
    setSortColumn('date_of_creation');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (safePage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (safePage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = safePage - 1; i <= safePage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const startIdx = (safePage - 1) * ITEMS_PER_PAGE;

  return (
    <Container fluid className="px-4">
      {/* Header Bar */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h4 className="mb-0"><i className="bi bi-receipt me-2"></i>Přehled objednávek</h4>
            </div>
            <div className="col-md-6 text-end">
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => fetchOrders()} title="Načíst znovu">
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
              <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} className="me-2" />
              <Button
                variant="primary"
                size="sm"
                onClick={() => openTab('order-new', 'Nová objednávka', '/orders/new', 'OrderForm')}
              >
                <i className="bi bi-plus-lg me-1"></i>Nová objednávka
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Filters and Table */}
      <div className="table-container">
        <div className="row g-3 align-items-end mb-4">
          <div className="col-12 col-md-3">
            <label className="form-label">Klient / médium / projekt / ...</label>
            <InputGroup>
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-search"></i>
              </span>
              <Form.Control
                className="border-start-0 search-box"
                placeholder="Hledat…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">Obchodník</label>
            <SearchableSelect
              options={responsiblePersons}
              value={responsiblePersonFilter}
              onChange={handleResponsiblePersonChange}
              placeholder="Hledat obchodníka…"
              emptyLabel="Všichni"
            />
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">Stav</label>
            <Form.Select value={stateFilter} onChange={handleStateChange}>
              <option value="">Vše</option>
              {states.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Od</label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Do</label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="col-12 col-md-1">
            <Button variant="outline-secondary" className="w-100" onClick={handleReset} title="Reset filtrů">
              <i className="bi bi-arrow-counterclockwise"></i>
            </Button>
          </div>
        </div>

        {/* Table with loading overlay */}
        <div className="table-loading-wrapper" style={{ position: 'relative' }}>
          {loading && (
            <div className="table-loading-overlay">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Načítání...</span>
              </div>
            </div>
          )}
          <div className="table-responsive grid-wrap" ref={wrapRef} style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease', pointerEvents: loading ? 'none' : 'auto' }}>
            <Table hover className="align-middle orders-table grid-resizable" style={tableStyle}>
              {colgroup && (
                <colgroup>
                  {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
              )}
              <thead className="table-light">
                <tr>
                  <th role="button" onClick={() => handleSort('number')}># <SortIcon column="number" /><span {...resizerProps('cislo')} /></th>
                  <th role="button" onClick={() => handleSort('date_of_creation')}>Vytvořeno <SortIcon column="date_of_creation" /><span {...resizerProps('vytvoreno')} /></th>
                  <th>Klient<span {...resizerProps('klient')} /></th>
                  <th>Médium<span {...resizerProps('medium')} /></th>
                  <th>Projekt<span {...resizerProps('projekt')} /></th>
                  <th>Inzeráty<span {...resizerProps('inzeraty')} /></th>
                  <th>Obchodník<span {...resizerProps('obchodnik')} /></th>
                  <th role="button" onClick={() => handleSort('state')}>Stav <SortIcon column="state" /><span {...resizerProps('stav')} /></th>
                  <th role="button" className="text-end" onClick={() => handleSort('total_price')}>Cena <SortIcon column="total_price" /><span {...resizerProps('cena')} /></th>
                  <th className="text-end">Akce<span {...resizerProps('akce')} /></th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => {
                  const stateStyle = getStateStyle(order.state);
                  const isCanceled = order.state === 'CANCELED';

                  return (
                    <tr key={order.id}>
                      <td><span className="small-muted order-link" role="button" title="Upravit objednávku" onClick={() => openOrderEdit(order)}>{order.number || order.id}</span></td>
                      <td><span className="small-muted">{formatDate(order.date_of_creation)}</span></td>
                      <td><span className="klient-cell order-link" role="button" title="Upravit objednávku" onClick={() => openOrderEdit(order)}>{order.customer_name}</span></td>
                      <td>
                        <MultiItemCell items={order.media} nameKey="name" />
                      </td>
                      <td>
                        <MultiItemCell items={order.projects} nameKey="name" />
                      </td>
                      <td className="inzeraty-cell">
                        <RatecardItemsCell items={order.ratecard_items} />
                      </td>
                      <td>
                        {order.responsible_person_name || <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <span
                          className="badge badge-stav"
                          style={{ backgroundColor: stateStyle.bg, color: stateStyle.color }}
                        >
                          {order.state_label || stateStyle.label}
                        </span>
                      </td>
                      <td className={`text-end fw-semibold ${isCanceled ? 'text-decoration-line-through text-muted' : ''}`}>
                        {formatPrice(order.total_price)} {order.currency || 'CZK'}
                      </td>
                      <td className="text-end text-nowrap">
                        <Button
                          variant="outline-primary"
                          className="btn-action me-1"
                          title="Upravit"
                          onClick={() => openOrderEdit(order)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        {/* Check mark icon - hidden for now */}
                        {/* <Button variant="outline-success" className="btn-action me-1" title="Schválit"><i className="bi bi-check-lg"></i></Button> */}
                        <Button
                          variant="outline-secondary"
                          className="btn-action me-1"
                          title="Duplikovat"
                          disabled
                        >
                          <i className="bi bi-files"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="btn-action"
                          title="Stáhnout PDF (potvrzení objednávky)"
                          onClick={() => window.open(api.getOrderPdfUrl(order.id), '_blank', 'noopener')}
                        >
                          <i className="bi bi-download"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && !loading && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      Žádné objednávky neodpovídají filtru
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <span className="text-muted">
              Zobrazeno: {orders.length > 0 ? startIdx + 1 : 0}–{startIdx + orders.length} z {totalItems}
            </span>
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={safePage === 1}>«</button>
              </li>
              <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={safePage === 1}>‹</button>
              </li>
              {getPageNumbers().map((page, idx) => (
                <li key={idx} className={`page-item ${page === safePage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                  {page === '...' ? (
                    <span className="page-link">...</span>
                  ) : (
                    <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                  )}
                </li>
              ))}
              <li className={`page-item ${safePage >= totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={safePage >= totalPages}>›</button>
              </li>
              <li className={`page-item ${safePage >= totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={safePage >= totalPages}>»</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </Container>
  );
}

export default OrdersList;
